/**
 * Rules and option lists for the CCA Manage Portfolio form — Add Instance,
 * templates and the few page-level CCA conditions. Pure functions and
 * constants, so every rule is testable without rendering.
 *
 * Why a CCA module next to components/eia/portfolioFormRules.js: CCA collects
 * different instance fields (Quantity + Total Number of Hours per Month instead
 * of the EIA usage figures), sends a different /input/validate row and offers
 * different templates. Everything that is the SAME in both products (portfolio
 * name / contract discount rules, OS and pricing-model lists, the OCI flex
 * test, the .xlsx upload check) is imported from the EIA module, never copied.
 *
 * Ported from legacy views/ManagePortfolio/ManageAccount.vue (CCA branch:
 * commonFields :323-401, fieldsForCCA :402-443, addInstanceClick :612-699,
 * clearInputFields :811-873) and UploadDownloadTemplate.vue (CCA templates
 * :29-57, :143-162). Line references point there.
 */
import { DEFAULT_OS, FIELD, isOciFlex } from '@components/eia/portfolioFormRules';

import awsCurTemplate from '@/assets/templates/AWS_CUR_Template.xlsx';
import azureCurTemplate from '@/assets/templates/AZURE_CUR_Template.xlsx';
import gcpCurTemplate from '@/assets/templates/GCP_CUR_Template.xlsx';
import instanceSkuTemplate from '@/assets/templates/Instance_SKU_Template.xlsx';
import ociTemplate from '@/assets/templates/OCI_Template.xlsx';

/* --- Field ids ------------------------------------------------------------ */

/**
 * CCA field ids — the API's own keys, as legacy sends them. Region, instance
 * type, OS, UUID, pricing model and OCI OCPU / Memory are the same keys EIA
 * uses; Quantity and Hours are CCA-only.
 */
export const CCA_FIELD = Object.freeze({
  REGION: FIELD.REGION,
  INSTANCE_TYPE: FIELD.INSTANCE_TYPE,
  OS: FIELD.OS,
  UUID: FIELD.UUID,
  OCPU: FIELD.OCPU,
  MEMORY: FIELD.MEMORY,
  QUANTITY: 'quantity',
  HOURS: 'monthly utilization (hourly)',
  PRICING_MODEL: FIELD.PRICING_MODEL,
});

/** Quantity and Hours never go negative (legacy sanitizeNonNegativeNumber :578-581). */
export const CCA_NUMBER_FIELDS = Object.freeze([
  CCA_FIELD.QUANTITY,
  CCA_FIELD.HOURS,
  CCA_FIELD.OCPU,
  CCA_FIELD.MEMORY,
]);

/* --- Labels and tooltips -------------------------------------------------- */

function isOci(provider) {
  return String(provider ?? '').toUpperCase() === 'OCI';
}

/** The instance-type field is "Size", or "Type" on OCI (ManageAccount.vue:340). */
export function instanceTypeLabel(provider) {
  return isOci(provider) ? 'Type' : 'Size';
}

/**
 * Each field's hover hint — legacy's CCA tooltips, verbatim (ManageAccount.vue
 * :333, :346, :358, :368, :379, :391, :411, :425). The UUID hint's line break
 * becomes ". ", as on EIA. Pricing Model has none in legacy.
 */
export const CCA_FIELD_TIPS = Object.freeze({
  [CCA_FIELD.REGION]: 'Regions associated with CSP.',
  [CCA_FIELD.INSTANCE_TYPE]: 'Instances associated with Region.',
  [CCA_FIELD.OS]: 'Select your Operating System',
  [CCA_FIELD.UUID]: 'UUID/Instance Name. For Ex: VM for AI/ML server.',
  [CCA_FIELD.OCPU]: 'Number of OCPUs for the OCI flex instance.',
  [CCA_FIELD.MEMORY]: 'Memory in GB for the OCI flex instance.',
  [CCA_FIELD.QUANTITY]: 'No. of similar sized instances',
  [CCA_FIELD.HOURS]: 'Total hours used for the month (quantity * 730)',
});

/**
 * The Add Instance fields, in legacy's two rows (ManageAccount.vue:71-118):
 *   row 1 `common` — Region, Size/Type, Operating System, UUID / Instance Name,
 *                    then OCPU + Memory on OCI (shown on OCI, disabled unless
 *                    the type is a flex shape, required when it is);
 *   row 2 `cca`    — Quantity, Total Number of Hours per Month, Pricing Model.
 *
 * `kind` is the control: 'select' | 'text' | 'number'. Option lists for the
 * selects are the page's (regions / types from the server, OS_OPTIONS and
 * PRICING_MODEL_OPTIONS from the EIA rules).
 *
 * @returns {{ common: object[], cca: object[] }}
 */
export function ccaFieldsFor(provider, instanceType) {
  const flex = isOciFlex(provider, instanceType);
  const field = (id, label, kind, extra = {}) =>
    Object.freeze({ id, label, kind, required: false, tooltip: CCA_FIELD_TIPS[id], ...extra });
  return {
    common: [
      field(CCA_FIELD.REGION, 'Region', 'select', { required: true }),
      field(CCA_FIELD.INSTANCE_TYPE, instanceTypeLabel(provider), 'select', { required: true }),
      field(CCA_FIELD.OS, 'Operating System', 'select'),
      field(CCA_FIELD.UUID, 'UUID / Instance Name', 'text'),
      ...(isOci(provider)
        ? [
            field(CCA_FIELD.OCPU, 'OCPU', 'number', { required: flex, disabled: !flex }),
            field(CCA_FIELD.MEMORY, 'Memory', 'number', { required: flex, disabled: !flex }),
          ]
        : []),
    ],
    cca: [
      field(CCA_FIELD.QUANTITY, 'Quantity', 'number', { required: true }),
      field(CCA_FIELD.HOURS, 'Total Number of Hours per Month', 'number', { required: true }),
      field(CCA_FIELD.PRICING_MODEL, 'Pricing Model', 'select', { required: true }),
    ],
  };
}

/* --- The form ------------------------------------------------------------- */

/**
 * An empty CCA Add Instance form — legacy's CCA reset (clearInputFields
 * :816-827), with '' for legacy's nulls so every input stays controlled.
 */
export function emptyCcaForm() {
  return {
    [CCA_FIELD.REGION]: '',
    [CCA_FIELD.INSTANCE_TYPE]: '',
    [CCA_FIELD.QUANTITY]: '',
    [CCA_FIELD.HOURS]: '',
    [CCA_FIELD.PRICING_MODEL]: '',
    [CCA_FIELD.UUID]: '',
    [CCA_FIELD.OS]: DEFAULT_OS,
    [CCA_FIELD.OCPU]: '',
    [CCA_FIELD.MEMORY]: '',
  };
}

/** What the form becomes after a successful add (legacy clearInputFields). */
export function resetCcaForm() {
  return emptyCcaForm();
}

/**
 * Which fields must be filled before Add Instance — legacy CCArequiredFields
 * (ManageAccount.vue:616-627; its `cloud_csp` is the page's provider, not a
 * field), plus OCPU / Memory for an OCI flex shape.
 */
export function requiredCcaFields(provider, instanceType) {
  return [
    CCA_FIELD.REGION,
    CCA_FIELD.INSTANCE_TYPE,
    CCA_FIELD.QUANTITY,
    CCA_FIELD.HOURS,
    CCA_FIELD.PRICING_MODEL,
    ...(isOciFlex(provider, instanceType) ? [CCA_FIELD.OCPU, CCA_FIELD.MEMORY] : []),
  ];
}

function blank(value) {
  return value === null || value === undefined || String(value).trim() === '';
}

/**
 * Quantity / Hours / OCPU / Memory: required, a number, never negative.
 * Legacy relied on `<input type="number" min="0">` plus stripping "-"
 * (:578-591); this is the same rule stated once, so a pasted value can't slip by.
 *
 * @returns {string | null}
 */
export function validateCcaNumber(value) {
  if (blank(value)) return 'Required';
  const n = Number(value);
  if (!Number.isFinite(n)) return 'Must be a number';
  if (n < 0) return 'Must be 0 or greater';
  return null;
}

/** "No. of similar sized instances". */
export function validateQuantity(value) {
  return validateCcaNumber(value);
}

/** "Total Number of Hours per Month". */
export function validateHours(value) {
  return validateCcaNumber(value);
}

/**
 * @returns {Record<string, string>} field id -> message, empty when valid.
 * 0 counts as entered (legacy's `!value` test saw the input's "0" string, which
 * is truthy).
 */
export function validateCcaForm(form, provider) {
  const errors = {};
  for (const field of requiredCcaFields(provider, form[CCA_FIELD.INSTANCE_TYPE])) {
    const value = form[field];
    let message = null;
    if (CCA_NUMBER_FIELDS.includes(field)) message = validateCcaNumber(value);
    else if (blank(value)) message = 'Required';
    if (message) errors[field] = message;
  }
  return errors;
}

/* --- The row sent to /input/validate ------------------------------------- */

/** A random RFC 4122 v4 UUID — legacy's `uuidv4()` (ManageAccount.vue:231, :672). */
export function randomUuid() {
  const c = globalThis.crypto;
  if (typeof c?.randomUUID === 'function') return c.randomUUID();
  // randomUUID is missing outside secure contexts (plain-http hosts) and in
  // older browsers; build the same v4 shape from random bytes.
  const bytes = new Uint8Array(16);
  if (typeof c?.getRandomValues === 'function') c.getRandomValues(bytes);
  else for (let i = 0; i < 16; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * The instance row exactly as legacy sends it for CCA (ManageAccount.vue
 * :664-677): the API's own key names, cloud_csp upper-cased, quantity / hours
 * as strings, a random UUID when none was typed, os_type lower-cased, OCPU /
 * Memory only for an OCI flex shape.
 *
 * @param {object} form      the Add Instance form (emptyCcaForm shape)
 * @param {string} provider  the selected cloud
 * @param {{ makeUuid?: () => string }} [options]  for tests
 */
export function buildCcaInstance(form, provider, { makeUuid = randomUuid } = {}) {
  const typedUuid = String(form[CCA_FIELD.UUID] ?? '').trim();
  const instance = {
    cloud_csp: String(provider ?? '').toUpperCase(),
    ...(form[CCA_FIELD.REGION] ? { region: form[CCA_FIELD.REGION] } : {}),
    ...(form[CCA_FIELD.INSTANCE_TYPE] ? { 'instance type': form[CCA_FIELD.INSTANCE_TYPE] } : {}),
    quantity: String(form[CCA_FIELD.QUANTITY] ?? ''),
    'monthly utilization (hourly)': String(form[CCA_FIELD.HOURS] ?? ''),
    pricingModel: form[CCA_FIELD.PRICING_MODEL],
    uuid: typedUuid || makeUuid(),
    os_type: String(form[CCA_FIELD.OS] || DEFAULT_OS).toLowerCase(),
  };
  if (isOciFlex(provider, form[CCA_FIELD.INSTANCE_TYPE])) {
    instance.ocpu = String(form[CCA_FIELD.OCPU] ?? '');
    instance.memory = String(form[CCA_FIELD.MEMORY] ?? '');
  }
  return instance;
}

/* --- Templates ------------------------------------------------------------ */

/**
 * The template control's label: legacy shows a "Template" button menu for an
 * Instance SKU portfolio and a "Templates" select in Billing (CUR) mode
 * (UploadDownloadTemplate.vue:31-43).
 */
export function ccaTemplateControlLabel({ billing = false } = {}) {
  return billing ? 'Templates' : 'Template';
}

/**
 * Instance SKU templates (UploadDownloadTemplate.vue:45-56): one per mode —
 * AWS/AZURE/GCP, or OCI on OCI. Imported, as the EIA templates are, so Vite
 * hashes them and a missing file fails the build.
 *
 * `filename` is the name the download is saved as. Legacy saved the first as
 * "AWS/AZURE/GCP.xlsx" — the slashes are not allowed in a file name, so
 * browsers mangled it; it is "AWS_AZURE_GCP.xlsx" here.
 */
const CCA_TEMPLATES_STANDARD = Object.freeze([
  Object.freeze({
    id: 'instances',
    label: 'AWS/AZURE/GCP Template',
    href: instanceSkuTemplate,
    filename: 'AWS_AZURE_GCP.xlsx',
  }),
]);

const CCA_TEMPLATES_OCI = Object.freeze([
  Object.freeze({
    id: 'oci-instances',
    label: 'OCI Template',
    href: ociTemplate,
    filename: 'OCI_Template.xlsx',
  }),
]);

/**
 * Billing (CUR reports) templates (UploadDownloadTemplate.vue:143-162) — all
 * three clouds, whichever Billing cloud is picked, as legacy lists them.
 */
const CCA_TEMPLATES_BILLING = Object.freeze([
  Object.freeze({
    id: 'aws-cur',
    label: 'AWS CUR Report Template',
    href: awsCurTemplate,
    filename: 'AWS_CUR_Template.xlsx',
  }),
  Object.freeze({
    id: 'azure-cur',
    label: 'AZURE CUR Report Template',
    href: azureCurTemplate,
    filename: 'AZURE_CUR_Template.xlsx',
  }),
  Object.freeze({
    id: 'gcp-cur',
    label: 'GCP CUR Report Template',
    href: gcpCurTemplate,
    filename: 'GCP_CUR_Template.xlsx',
  }),
]);

/**
 * The CCA download templates — same `{ id, label, href, filename }` shape as
 * the EIA `templatesFor`, so `downloadTemplate` (components/eia) saves them.
 */
export function ccaTemplatesFor(provider, { billing = false } = {}) {
  if (billing) return CCA_TEMPLATES_BILLING;
  return isOci(provider) ? CCA_TEMPLATES_OCI : CCA_TEMPLATES_STANDARD;
}

/* --- Page-level CCA conditions -------------------------------------------- */

/** Legacy's benchmark model for the CCA-only "General IT Average" (and its alias). */
const SCALER_MODELS = Object.freeze(['General IT Average', 'Scaler']);

/**
 * Is the "Cloud Usage Reports" link shown? (ManageAccount.vue:51): an Instance
 * SKU portfolio (not Billing), not General IT Average / Scaler, not OCI. The
 * `manage_portfolio.cloud-accounts` permission is the page's to check.
 */
export function showsCloudUsageReports({ billing = false, model, provider } = {}) {
  return !billing && !SCALER_MODELS.includes(model) && !isOci(provider);
}

/**
 * Policy Engine: legacy never shows a picker, so it always sends the opened
 * portfolio's saved value or this default (store selectedPolicyType;
 * UploadDownloadTemplate.vue:236-244, :410).
 */
export const DEFAULT_POLICY_ENGINE = 'No Policy Engine (Default)';

export function policyEngineFor(savedValue) {
  return typeof savedValue === 'string' && savedValue.trim() ? savedValue : DEFAULT_POLICY_ENGINE;
}
